/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * `RedactionPlaceholder` — a correctly-sized, inline stand-in rendered while the
 * redaction extension handler / `ScribbleBar` component is still resolving.
 *
 * The redaction node is rendered through the async extension pipeline
 * (`render: async () => RedactionMarker`), so there is a brief window before the
 * real `ScribbleBar` mounts. Without a sized placeholder the node collapses to
 * empty text (and can briefly wrap), then jumps to the final scribble width once
 * it mounts, producing a visible layout shift. This placeholder reserves the
 * exact inline footprint the `ScribbleBar` will occupy so the surrounding text
 * never moves; the scribble simply fades in over the reserved box.
 *
 * The box dimensions here MUST stay in sync with the `ScribbleBar` container in
 * `./index.tsx` (`height`, `aspectRatio`, `display`, `verticalAlign`,
 * `borderRadius`). Any change to the bar's size must be mirrored here.
 */
import React from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	placeholder: {
		// Keep these values identical to `ScribbleBar`'s container box so the
		// reserved space matches the final rendered bar exactly (no layout shift).
		display: 'inline-flex',
		height: '1.125rem',
		aspectRatio: '3.4 / 1',
		verticalAlign: 'middle',
		borderRadius: token('radius.xsmall'),
		// Neutral, non-animated fill so the reserved box is unobtrusive while the
		// scribble loads. Redacted content must not be selectable.
		backgroundColor: token('color.background.neutral'),
		userSelect: 'none',
	},
});

/**
 * Inline, non-interactive placeholder that reserves the `ScribbleBar` footprint
 * during load. Hidden from assistive tech since it carries no meaning on its own
 * (the mounted bar provides the accessible label).
 */
export const RedactionPlaceholder = (): React.JSX.Element => (
	<span css={styles.placeholder} data-testid="guard-redaction-placeholder" aria-hidden="true" />
);
